import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, DoorOpen, ArrowRight } from "lucide-react";

/**
 * PublicRoom — /room/:slug
 *
 * A15: resolves a teacher's `profile_slug` to their currently-live class
 * (creating a walk-in room if none). Signed-in users are redirected into
 * /live/<id>; anonymous visitors are sent to the guest join flow.
 */
export default function PublicRoom() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, error: "", teacher: null, cls: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/rooms/${slug}`);
        setState({ loading: false, error: "", teacher: data.teacher, cls: data.class });
      } catch (e) {
        setState({ loading: false, error: e.response?.data?.detail || "Room not found", teacher: null, cls: null });
      }
    })();
  }, [slug]);

  const enter = async () => {
    if (!state.cls) return;
    if (user) {
      // Enroll signed-in students on the fly so live-state polling works
      try { await api.post(`/classes/${state.cls.id}/self-join`); } catch { /* noop */ }
      nav(`/live/${state.cls.id}`);
    } else if (state.cls.guest_token) {
      nav(`/guest/${state.cls.id}?token=${state.cls.guest_token}`);
    } else {
      nav(`/guest/${state.cls.id}`);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBFAF7]">
        <Loader2 size={24} className="animate-spin text-neutral-500" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBFAF7] p-6">
        <div className="text-center max-w-md">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ room</div>
          <h1 className="font-display font-bold text-3xl mt-2">Room not found</h1>
          <p className="text-neutral-500 mt-3">The teacher's permanent room link may have changed, or the account is inactive.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBFAF7] p-6" data-testid="public-room-page">
      <div className="text-center max-w-lg">
        <DoorOpen size={40} className="text-[#FFB400] mx-auto" />
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mt-4">/ live · walk-in room</div>
        <h1 className="font-display font-bold text-4xl tracking-tighter mt-2">{state.teacher.full_name}</h1>
        <p className="text-neutral-500 mt-2">is inviting you to <strong className="text-[#0A0C10]">{state.cls.title}</strong></p>
        <button
          onClick={enter}
          data-testid="public-room-enter"
          className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#0A0C10] text-white text-sm font-bold hover:bg-[#0A0C10]/90"
        >
          Enter the classroom <ArrowRight size={14} />
        </button>
        {!user && <div className="mt-4 text-xs text-neutral-500">No account needed — you'll join as a guest.</div>}
      </div>
    </div>
  );
}
